import { getApiBaseUrl } from "@apps/dashboard/lib/env";
import type { Locale } from "@project/i18n";
import { localeToAcceptLanguageHeader } from "@project/i18n";

export interface ApiErrorBody {
  statusCode: number;
  key?: string;
  message?: string;
  params?: Record<string, string | number>;
}

export class ApiRequestError extends Error {
  constructor(
    readonly statusCode: number,
    readonly key: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function acceptLanguage(locale: Locale): string {
  return localeToAcceptLanguageHeader(locale);
}

function buildUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  locale?: Locale;
  /** Objeto serializado como JSON */
  jsonBody?: unknown;
}

export async function apiFetchJson<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { locale = "es", jsonBody, headers: initHeaders, ...rest } = options;
  const headers = new Headers(initHeaders);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  headers.set("Accept-Language", acceptLanguage(locale));
  if (jsonBody !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const body = jsonBody !== undefined ? JSON.stringify(jsonBody) : undefined;

  const res = await fetch(buildUrl(path), {
    ...rest,
    headers,
    credentials: "include",
    body,
  });

  const payload = await parseJsonSafe(res);

  if (!res.ok) {
    const body = payload as ApiErrorBody | null;
    const msg =
      typeof body?.message === "string" && body.message.length > 0
        ? body.message
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiRequestError(res.status, body?.key, msg);
  }

  return payload as T;
}

export async function apiGet<T>(path: string, locale: Locale = "es"): Promise<T> {
  return apiFetchJson<T>(path, { method: "GET", locale });
}

export async function apiPost<T>(path: string, jsonBody: unknown, locale: Locale = "es"): Promise<T> {
  return apiFetchJson<T>(path, { method: "POST", locale, jsonBody });
}

export async function apiPut<T>(path: string, jsonBody: unknown, locale: Locale = "es"): Promise<T> {
  return apiFetchJson<T>(path, { method: "PUT", locale, jsonBody });
}

export async function apiPatch<T>(path: string, jsonBody: unknown, locale: Locale = "es"): Promise<T> {
  return apiFetchJson<T>(path, { method: "PATCH", locale, jsonBody });
}

export async function apiDelete<T>(path: string, locale: Locale = "es"): Promise<T> {
  return apiFetchJson<T>(path, { method: "DELETE", locale });
}

/** Multipart sin `Content-Type` manual (el navegador añade boundary). */
export async function apiPostFormData<T>(path: string, formData: FormData, locale: Locale = "es"): Promise<T> {
  const headers = new Headers();
  headers.set("Accept", "application/json");
  headers.set("Accept-Language", acceptLanguage(locale));

  const res = await fetch(buildUrl(path), {
    method: "POST",
    headers,
    credentials: "include",
    body: formData,
  });

  const payload = await parseJsonSafe(res);

  if (!res.ok) {
    const body = payload as ApiErrorBody | null;
    const msg =
      typeof body?.message === "string" && body.message.length > 0
        ? body.message
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiRequestError(res.status, body?.key, msg);
  }

  return payload as T;
}
